import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect } from 'react';
import { IconButton } from '@mui/material';

import usePushDataLayerEvent from '../../../../utils/google-analytics.util';
import { handleToggleChat, showToast } from '../../../../store/slices/generalSlice';
import storageService from '../../../../services/storage.service';
import { IMetadata } from '../../../../models/metadata.model';
import useAssistance from '../../../../hooks/useAssistance/useAssistance';
import { theme } from '../../../../global-styles/colors';
import { Heading2Text, Subhead02Text } from '../../../../components/typography/typography';
import ImbeeChat from '../../../../components/imbee-chat/ImbeeChat';
import AlxButton from '../../../../components/alx-components/button';
import { images } from '../../../../assets/images/images';
import { icons } from '../../../../assets/icons/icons';
import { ConfirmationErrorStyle } from './confirmation-error-style';

const ConfirmationError = () => {
  const { t } = useTranslation(['common', 'auth', 'aia']);
  const { intervention, identifier } = useParams<{ intervention: string; identifier: string; state: string }>();
  const { requestACallMutation, requestIntervention } = useAssistance({ intervention });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pushDataLayerEvent } = usePushDataLayerEvent();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data, isLoading, isError } = requestIntervention;

  const returnToService = useCallback(() => {
    navigate(`/unregistered/${intervention}/${identifier}`);
  }, [navigate, intervention, identifier]);

  useEffect(() => {
    if (data) {
      const checkAndWriteMetadata = async () => {
        const metadata = await storageService.getItem('metaData');
        if (!metadata) {
          const metaData: IMetadata = {
            interventionNumber: data.intervencion,
            carIdentification: data.matricula,
            name: data.personaContacto,
            assistanceNumber: identifier,
            userIdentification: '',
          };
          await storageService.setItem('metaData', metaData);
        }
      };
      void checkAndWriteMetadata();
    }
  }, [data, identifier]);

  useEffect(() => {
    // grua-no-ha-llegado
    pushDataLayerEvent('screen_view', null, 'grua-no-ha-llegado');
  }, [pushDataLayerEvent]);

  const handleLaunchChat = useCallback(() => {
    dispatch(handleToggleChat());
    pushDataLayerEvent('solicitar_ayuda', { metodo_contacto: 'chat' }, 'grua-no-ha-llegado');
  }, [dispatch, pushDataLayerEvent]);

  return (
    <ConfirmationErrorStyle>
      <div className="go-back">
        <IconButton className={'secondary '} onClick={returnToService} data-cy="back-step-button">
          <img src={icons.arrowLeftWhiteIcon} alt="" />
        </IconButton>
      </div>
      <Heading2Text className="m-24px" as="h1" weight={600} color={theme.neutrals.white}>
        {t('aia:confirmation.error.title')}
      </Heading2Text>
      <Subhead02Text className="m-24px" color={theme.neutrals.white}>
        {t('aia:confirmation.error.description')}
      </Subhead02Text>
      <Subhead02Text className="m-24px" color={theme.neutrals.white}>
        {t('aia:confirmation.error.description-2')}
      </Subhead02Text>
      <AlxButton className="secondary m-24px" onClick={() => handleLaunchChat()}>
        {t('aia:confirmation.error.talk-through-chat')}
      </AlxButton>
      <AlxButton
        className="borderWhite m-24px"
        onClick={() => {
          if (intervention) {
            requestACallMutation.mutate(intervention, {
              onSuccess: () => {
                dispatch(
                  showToast({ severity: 'success', open: true, message: t('aia:confirmation.call-me-back.title') }),
                );
                pushDataLayerEvent('solicitar_ayuda', { metodo_contacto: 'phone' }, 'grua-no-ha-llegado');
                returnToService();
              },
              onError: () => {
                pushDataLayerEvent(
                  'error',
                  {
                    mensaje_error: 'No se ha podido registrar la mutación',
                  },
                  'grua-no-ha-llegado',
                );
              },
            });
          }
        }}
      >
        {t('aia:confirmation.error.call-me')}
      </AlxButton>
      <div className="confirmation-img">
        <img src={images.cars} alt="" />
      </div>
      <ImbeeChat onClose={returnToService} chatVariables={[]} />
    </ConfirmationErrorStyle>
  );
};

export default ConfirmationError;
